import { Link } from "@tanstack/react-router";
import { SiteLayout } from "@/components/SiteLayout";
import {
  ArrowRight,
  ClipboardCheck,
  HardHat,
  Check,
  FileSearch,
  Calculator,
  ShieldCheck,
  ClipboardList,
  Wrench,
  Users,
  Handshake,
  type LucideIcon,
} from "lucide-react";
import { type Locale } from "@/i18n/config";
import { pathFor } from "@/i18n/slugMap";
import { BASE_URL, pageHead } from "@/i18n/head";
import { serviceSchema } from "@/lib/schema";
import { byggeledelseStrings } from "@/i18n/content/byggeledelse";
import { Img, SIZES } from "@/components/Img";

/** <head> for the construction-management service page: pageHead + Service schema. */
export function byggeledelseHead(locale: Locale) {
  const s = byggeledelseStrings[locale];
  return pageHead("byggeledelse", locale, s.meta, {
    breadcrumbName: s.hero.title,
    schema: [
      serviceSchema({
        name: s.hero.title,
        serviceType: s.hero.title,
        description: s.meta.description,
        url: BASE_URL + pathFor("byggeledelse", locale),
        image: s.meta.ogImage,
        audienceType: "Bygherrer, industri, life science, offentlige bygherrer",
      }),
    ],
  });
}

const HERO_IMG = "/wp-content/uploads/MISupportIT_NTK2628_1350x900-1.jpg";

// Icons aligned by index with the phase card arrays in the catalog.
const phase1Icons: LucideIcon[] = [FileSearch, ShieldCheck, ClipboardList];
const phase2Icons: LucideIcon[] = [Users, Calculator, ShieldCheck, Wrench, ClipboardList, Handshake];
const phase3Icons: LucideIcon[] = [ClipboardCheck, Wrench, ClipboardList];

function PhaseCards({
  cards,
  icons,
}: {
  cards: { title: string; items: string[] }[];
  icons: LucideIcon[];
}) {
  return (
    <div className="mt-8 grid gap-5 md:grid-cols-2 xl:grid-cols-3">
      {cards.map((card, i) => {
        const Icon = icons[i] ?? ClipboardList;
        return (
          <div
            key={card.title}
            className="group rounded-2xl border border-section-light-foreground/10 bg-section-light text-section-light-foreground p-6 hover:border-primary/50 hover:shadow-lg transition-all"
          >
            <div className="flex items-center gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/15 text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                <Icon className="h-5 w-5" />
              </span>
              <h4 className="text-base font-bold tracking-tight">{card.title}</h4>
            </div>
            <ul className="mt-4 space-y-2.5">
              {card.items.map((item) => (
                <li key={item} className="flex items-start gap-2.5 text-sm text-section-light-foreground/85 leading-relaxed">
                  <Check className="mt-1 h-3.5 w-3.5 shrink-0 text-primary" strokeWidth={3} />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        );
      })}
    </div>
  );
}

export function ByggeledelsePage({ locale }: { locale: Locale }) {
  const t = byggeledelseStrings[locale];

  return (
    <SiteLayout>
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <Img src={HERO_IMG} alt={t.hero.title} className="h-full w-full object-cover" sizes={SIZES.full} priority />
          <div className="absolute inset-0 bg-gradient-to-r from-background via-background/85 to-background/40" />
        </div>
        <div className="relative mx-auto max-w-7xl px-4 md:px-8 py-24 md:py-36">
          <p className="text-sm uppercase tracking-widest text-primary font-semibold">{t.hero.eyebrow}</p>
          <h1 className="mt-4 text-4xl md:text-6xl font-bold tracking-tight max-w-3xl">{t.hero.title}</h1>
          <p className="mt-6 text-lg md:text-xl text-foreground/85 leading-relaxed max-w-2xl">{t.hero.body}</p>
          <div className="mt-10 flex flex-wrap gap-4">
            <Link to={pathFor("kontakt", locale)} className="btn-cta">
              {t.hero.ctaPrimary} <ArrowRight className="h-4 w-4" />
            </Link>
            <Link
              to={pathFor("ydelser", locale)}
              className="inline-flex items-center gap-2 rounded-full border border-white/20 px-8 py-3.5 text-base font-semibold text-foreground hover:bg-white/5"
            >
              {t.hero.ctaSecondary}
            </Link>
          </div>
        </div>
      </section>

      {/* Intro */}
      <section className="py-20 md:py-24">
        <div className="mx-auto max-w-7xl px-4 md:px-8 grid gap-12 md:grid-cols-2 items-center">
          <div>
            <p className="text-sm uppercase tracking-widest text-foreground/70">{t.intro.eyebrow}</p>
            <h2 className="mt-3 text-3xl md:text-4xl font-bold tracking-tight">{t.intro.heading}</h2>
            <p className="mt-6 text-lg text-foreground/85 leading-relaxed">{t.intro.p1}</p>
            <p className="mt-4 text-lg text-foreground/85 leading-relaxed">{t.intro.p2}</p>
            <Link to={pathFor("kontakt", locale)} className="mt-8 btn-cta">
              {t.intro.cta} <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <Img src={HERO_IMG} alt={t.hero.title} className="rounded-2xl w-full aspect-[4/3] object-cover" sizes={SIZES.split} />
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16">
        <div className="mx-auto max-w-7xl px-4 md:px-8">
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-center mb-4">{t.benefits.heading}</h2>
          <p className="text-center text-foreground/75 leading-relaxed max-w-3xl mx-auto mb-10">{t.benefits.intro}</p>
          <div className="grid gap-8 md:grid-cols-3">
            {t.benefits.cards.map((c) => (
              <div key={c.title} className="rounded-2xl bg-secondary p-8">
                <h3 className="text-xl font-bold">{c.title}</h3>
                <p className="mt-4 text-foreground/90 leading-relaxed">{c.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Phases */}
      <section className="bg-section-light text-section-light-foreground py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-4 md:px-8">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <p className="text-sm uppercase tracking-widest text-foreground/60">{t.phases.eyebrow}</p>
            <h2 className="mt-3 text-3xl md:text-4xl font-bold tracking-tight">{t.phases.heading}</h2>
            <p className="mt-4 text-section-light-foreground leading-relaxed">{t.phases.intro}</p>
          </div>

          {/* Phase 01 */}
          <article className="relative rounded-3xl bg-background text-foreground border border-foreground/10 p-8 md:p-10 shadow-sm">
            <div className="flex items-start gap-5">
              <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-primary text-primary-foreground shadow-md">
                <ClipboardCheck className="h-7 w-7" />
              </div>
              <div className="min-w-0">
                <p className="text-xs font-semibold uppercase tracking-widest text-primary">{t.phase1.eyebrow}</p>
                <h3 className="mt-1 text-2xl md:text-3xl font-bold tracking-tight">{t.phase1.title}</h3>
              </div>
            </div>
            <p className="mt-6 text-foreground/80 leading-relaxed">{t.phase1.lead}</p>
            <PhaseCards cards={t.phase1.cards} icons={phase1Icons} />
          </article>

          {/* Phase 02 */}
          <article className="relative mt-8 rounded-3xl bg-background text-foreground border border-foreground/10 p-8 md:p-10 shadow-sm">
            <div className="flex items-start gap-5">
              <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-primary text-primary-foreground shadow-md">
                <HardHat className="h-7 w-7" />
              </div>
              <div className="min-w-0">
                <p className="text-xs font-semibold uppercase tracking-widest text-primary">{t.phase2.eyebrow}</p>
                <h3 className="mt-1 text-2xl md:text-3xl font-bold tracking-tight">{t.phase2.title}</h3>
                <p className="mt-3 text-foreground/80 leading-relaxed">{t.phase2.lead}</p>
              </div>
            </div>
            <PhaseCards cards={t.phase2.cards} icons={phase2Icons} />
          </article>

          {/* Phase 03 */}
          <article className="relative mt-8 rounded-3xl bg-background text-foreground border border-foreground/10 p-8 md:p-10 shadow-sm">
            <div className="flex items-start gap-5">
              <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-primary text-primary-foreground shadow-md">
                <Handshake className="h-7 w-7" />
              </div>
              <div className="min-w-0">
                <p className="text-xs font-semibold uppercase tracking-widest text-primary">{t.phase3.eyebrow}</p>
                <h3 className="mt-1 text-2xl md:text-3xl font-bold tracking-tight">{t.phase3.title}</h3>
                <p className="mt-3 text-foreground/80 leading-relaxed">{t.phase3.lead}</p>
              </div>
            </div>
            <PhaseCards cards={t.phase3.cards} icons={phase3Icons} />
          </article>
        </div>
      </section>

      {/* Quote 1 */}
      <section className="py-20">
        <div className="mx-auto max-w-4xl px-4 md:px-8 text-center">
          <p className="text-xl md:text-2xl italic leading-relaxed text-foreground/90">“{t.quote1.text}”</p>
          <p className="mt-4 text-sm uppercase tracking-widest text-foreground/70">{t.quote1.attrib}</p>
        </div>
      </section>

      {/* Early involvement */}
      <section className="py-20">
        <div className="mx-auto max-w-7xl px-4 md:px-8 grid gap-10 md:grid-cols-2 items-center">
          <div>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight">{t.early.heading}</h2>
            <p className="mt-6 text-lg text-foreground/85 leading-relaxed">{t.early.p1}</p>
            <p className="mt-4 text-lg text-foreground/85 leading-relaxed">{t.early.p2}</p>
            <p className="mt-4 text-lg text-foreground/85 leading-relaxed">{t.early.p3}</p>
            <Link to={pathFor("kontakt", locale)} className="mt-8 btn-cta">
              {t.early.cta} <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <Img
            src="/wp-content/uploads/MISupportIT_NTK4202_1350x900.jpg"
            alt={t.early.heading}
            className="rounded-2xl w-full aspect-[4/3] object-cover"
            sizes={SIZES.split}
          />
        </div>
      </section>

      {/* Safety */}
      <section className="bg-section-light text-section-light-foreground py-20">
        <div className="mx-auto max-w-4xl px-4 md:px-8">
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight">{t.safety.heading}</h2>
          <p className="mt-6 text-lg leading-relaxed text-section-light-foreground/85">{t.safety.p1}</p>
          <p className="mt-4 text-lg leading-relaxed text-section-light-foreground/85">{t.safety.p2}</p>
          <p className="mt-4 text-lg leading-relaxed text-section-light-foreground/85">{t.safety.p3}</p>
          <Link to={pathFor("hse", locale)} className="mt-8 btn-cta">
            {t.safety.cta} <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      {/* Quote 2 */}
      <section className="py-16">
        <div className="mx-auto max-w-4xl px-4 md:px-8 text-center">
          <p className="text-xl md:text-2xl italic leading-relaxed text-foreground/90">“{t.quote2.text}”</p>
          <p className="mt-4 text-sm uppercase tracking-widest text-foreground/70">{t.quote2.attrib}</p>
        </div>
      </section>

      {/* Pharma */}
      <section className="py-20">
        <div className="mx-auto max-w-7xl px-4 md:px-8 grid gap-10 md:grid-cols-2 items-center">
          <Img
            src="/wp-content/uploads/Kontor-gruppe-15_1400x1400.jpg"
            alt={t.pharma.heading}
            className="rounded-2xl w-full aspect-square object-cover"
            sizes={SIZES.split}
          />
          <div>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight">{t.pharma.heading}</h2>
            <p className="mt-6 text-lg text-foreground/85 leading-relaxed">{t.pharma.p1}</p>
            <p className="mt-4 text-lg text-foreground/85 leading-relaxed">{t.pharma.p2}</p>
            <p className="mt-4 text-lg text-foreground/85 leading-relaxed">{t.pharma.p3}</p>
            <Link to={pathFor("samarbejdspartnere", locale)} className="mt-8 btn-cta">
              {t.pharma.cta} <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Expertise */}
      <section className="py-20">
        <div className="mx-auto max-w-7xl px-4 md:px-8 grid gap-10 md:grid-cols-2 items-center">
          <div>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight">{t.expertise.heading}</h2>
            <p className="mt-6 text-lg text-foreground/85 leading-relaxed">{t.expertise.p1}</p>
            <p className="mt-4 text-lg text-foreground/85 leading-relaxed">{t.expertise.p2}</p>
            <p className="mt-6 text-lg text-foreground/85 leading-relaxed">{t.expertise.leadIn}</p>
            <ul className="mt-4 space-y-3">
              {t.expertise.items.map((item) => (
                <li key={item} className="flex items-start gap-3 text-foreground/85">
                  <Check className="mt-1 h-4 w-4 shrink-0 text-primary" strokeWidth={3} />
                  <span className="leading-relaxed">{item}</span>
                </li>
              ))}
            </ul>
            <Link to={pathFor("modenmisupporter", locale)} className="mt-8 btn-cta">
              {t.expertise.cta} <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <Img
            src="/wp-content/uploads/Dan-Container3_1350x900-scaled-1.jpg"
            alt={t.expertise.heading}
            className="rounded-2xl w-full aspect-[4/3] object-cover"
            sizes={SIZES.split}
          />
        </div>
      </section>

      {/* CTA */}
      <section className="bg-section-light text-section-light-foreground py-20">
        <div className="mx-auto max-w-4xl px-4 md:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight">{t.cta.heading}</h2>
          <p className="mt-6 text-lg leading-relaxed">{t.cta.body}</p>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
            <a
              href="tel:+4555551212"
              className="inline-flex items-center gap-2 rounded-2xl border-2 border-secondary bg-transparent text-secondary px-9 py-4 text-base font-semibold transition-colors hover:bg-secondary hover:text-white"
            >
              {t.cta.call}
            </a>
            <Link to={pathFor("kontakt", locale)} className="btn-cta">
              {t.cta.write} <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}
